import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  inject,
  input,
  model,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Event, NavigationEnd, Router, RouterModule } from '@angular/router';
import { FeatureFlag } from '@app/_shared/enum/feature-flag.enum';
import { DataObject } from '@app/_shared/interfaces';
import { Stores } from '@app/_shared/interfaces/http/stores';
import { FontAwesomeImportModule } from '@app/_shared/material/font-awesome-import.module';
import { CustomPipeModule } from '@app/_shared/pipes/custom-pipe.module';
import { SupportLinksService } from '@app/_shared/service';
import {
  AccountingGroup,
  RouteGroup,
  RouteMappingService,
  ToolboxGroup,
} from '@app/_shared/service/route-mapping.service';
import { ThemeService } from '@app/_shared/service/theme.service';
import * as BootstrapActions from '@app/main-store/bootstrap.actions';
import { getActiveStores, getDefaultStore, hasOnlyShopifyBillingService } from '@app/main-store/bootstrap.selector';
import { companyFeatureFlags } from '@app/main-store/feature-flags.selectors';
import { hasShopifySalesChannel } from '@app/main-store/integrations/integrations.selectors';
import { AuthService } from '@auth0/auth0-angular';
import { environment } from '@env/environment';
import { Store, select } from '@ngrx/store';
import { MenuItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DividerModule } from 'primeng/divider';
import { MenuModule } from 'primeng/menu';
import { PanelMenuModule } from 'primeng/panelmenu';
import { TooltipModule } from 'primeng/tooltip';
import { Observable, combineLatest, map } from 'rxjs';
import { filter } from 'rxjs/operators';
import { SearchBarModule } from '../search-bar/search-bar.module';

interface ExtendedMenuItem extends MenuItem {
  showAddIcon?: boolean;
  showWarning?: boolean;
  showSettingsButton?: boolean;
  settingsCommand?: (event: any) => void;
}

@Component({
  selector: 'sb-sidebar-menu',
  templateUrl: './sidebar-menu.component.html',
  imports: [
    CommonModule,
    RouterModule,
    FontAwesomeImportModule,
    CustomPipeModule,
    ButtonModule,
    DividerModule,
    MenuModule,
    PanelMenuModule,
    TooltipModule,
    SearchBarModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidebarMenuComponent implements OnInit {
  private _authService = inject(AuthService);
  private supportLinkService = inject(SupportLinksService);
  private router = inject(Router);
  private rxStore = inject<Store<any>>(Store);
  private themeService = inject(ThemeService);
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);
  private routeMapping = inject(RouteMappingService);

  readonly activeRouteGroup = input<RouteGroup>('accounting');
  readonly activeAccountingGroup = input<AccountingGroup>('orders');
  readonly activeToolboxGroup = input<ToolboxGroup>('balance');

  readonly activeStoreId = model<string | number | null>(null);

  public panelMenuItems = signal<MenuItem[]>([]);
  public accountMenuItems = signal<MenuItem[]>([]);
  public businessMenuItems = signal<ExtendedMenuItem[]>([]);

  public stores$: Observable<DataObject<Stores>[]> = this.rxStore.pipe(
    takeUntilDestroyed(this.destroyRef),
    select(getActiveStores),
  );

  public defaultStore$: Observable<DataObject<Stores> | null> = this.rxStore.pipe(
    takeUntilDestroyed(this.destroyRef),
    select(getDefaultStore),
    map((store) => {
      if (store?.id) {
        this.activeStoreId.set(store.id);
      }
      return store;
    }),
  );

  public defaultStoreId$: Observable<number | null> = this.defaultStore$.pipe(
    map((store) => (store?.id ? +store.id : null)),
  );

  public hasOnlyShopifyBillingService$ = this.rxStore.select(hasOnlyShopifyBillingService);
  public featureFlags$ = this.rxStore.select(companyFeatureFlags);
  public hasShopifySalesChannel$ = this.rxStore.select(hasShopifySalesChannel);

  ngOnInit(): void {
    this.setupPanelMenuItems();
    this.setupAccountMenuItems();
    this.setupBusinessMenuItems();

    this.hasShopifySalesChannel$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((hasShopify) => {
      // this.updateReportsMenuItem(hasShopify, this.activeToolboxGroup());
      this.cdr.markForCheck();
    });

    this.router.events
      .pipe(
        filter((event: Event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event: NavigationEnd) => {
        this.updateActiveMenuItems(event.urlAfterRedirects);
        this.cdr.markForCheck();
      });

    this.updateActiveMenuItems(this.router.url);
  }

  // updateReportsMenuItem(hasShopify: boolean, activeToolboxGroup: ToolboxGroup): void {
  //   const updatedPanelMenuItems = this.panelMenuItems().map((item) => {
  //     if (item.id === 'toolbox') {
  //       const updatedItems = item.items?.filter((subItem) => subItem.id !== 'reports') || [];

  //       if (hasShopify) {
  //         updatedItems.push({
  //           id: 'reports',
  //           label: 'Rapporter',
  //           icon: 'file-chart-column',
  //           iconPrefix: 'fal',
  //           routerLink: ['/rapporter'],
  //           active: activeToolboxGroup === 'reports',
  //         });
  //       }

  //       return {
  //         ...item,
  //         items: updatedItems,
  //       };
  //     }
  //     return item;
  //   });

  //   this.panelMenuItems.set(updatedPanelMenuItems);
  // }

  setupPanelMenuItems(): void {
    const menuItems: MenuItem[] = [
      {
        id: 'home',
        label: 'Hjem',
        icon: 'house',
        iconPrefix: 'fal',
        command: () => this.router.navigate(['/']),
      },
      {
        id: 'accounting',
        label: 'Bogføring',
        icon: 'calculator',
        iconPrefix: 'fal',
        items: [
          {
            id: 'orders',
            label: 'Salg',
            icon: 'basket-shopping',
            iconPrefix: 'fal',
            routerLink: ['/ordrer'],
          },
          {
            id: 'payments',
            label: 'Betalinger',
            icon: 'money-bill-transfer',
            iconPrefix: 'fal',
            routerLink: ['/betalinger/gateway'],
          },
          {
            id: 'repayments',
            label: 'Tilbagebetalinger',
            icon: 'money-bill-wave',
            iconPrefix: 'fal',
            routerLink: ['/tilbage-betalinger/gateway'],
          },
          {
            id: 'corrections',
            label: 'Korrektioner',
            icon: 'money-check-pen',
            iconPrefix: 'fal',
            routerLink: ['/korrektioner'],
          },
          {
            id: 'bundles',
            label: 'Samleposteringer',
            icon: 'file-export',
            iconPrefix: 'fal',
            routerLink: ['/bundles'],
          },
          {
            id: 'payouts',
            label: 'Udbetalinger',
            icon: 'piggy-bank',
            iconPrefix: 'fal',
            routerLink: ['/bank'],
          },
        ],
      },
      {
        id: 'toolbox',
        label: 'Værktøjer',
        icon: 'screwdriver-wrench',
        iconPrefix: 'fal',
        items: [
          {
            id: 'balance',
            label: 'Ordrebalance',
            icon: 'scale-unbalanced',
            iconPrefix: 'fal',
            routerLink: ['/balances/orders-postings'],
          },
          {
            id: 'account-balance',
            label: 'Saldobalance',
            icon: 'calculator-simple',
            iconPrefix: 'fal',
            routerLink: ['/saldobalance'],
          },
        ],
      },
    ];

    this.panelMenuItems.set(menuItems);

    this.featureFlags$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((flags) => {
      const hasBankIntegrationsFlag = flags.some((flag) => flag.attributes.key === FeatureFlag.BankIntegrations);

      const updatedItems = this.panelMenuItems().map((item) => {
        if (item.id === 'accounting' && item.items) {
          const filteredItems = item.items.filter((subItem) => !(subItem.label === 'Bank transaktioner'));

          if (hasBankIntegrationsFlag) {
            filteredItems.push({
              id: 'gc-transactions',
              label: 'Bank transaktioner',
              icon: 'building-columns',
              iconPrefix: 'fal',
              routerLink: ['/transaktioner'],
              active: this.activeAccountingGroup() === 'gc-transactions',
            });
          }

          return { ...item, items: filteredItems };
        }
        return item;
      });

      this.panelMenuItems.set(updatedItems);
      this.cdr.markForCheck();
    });
  }

  setupAccountMenuItems(): void {
    const baseItems: MenuItem[] = [
      { label: 'Firma', routerLink: ['/konto/profil'] },
      { label: 'Brugerprofil', routerLink: ['/konto/brugerprofil'] },
      { label: 'Team', routerLink: ['/konto/team'] },
    ];

    this.accountMenuItems.set(baseItems);

    this.hasOnlyShopifyBillingService$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((hasOnly) => {
      let items = [...baseItems];

      if (!hasOnly) {
        items = [
          ...items,
          { label: 'Betalingskort', routerLink: ['/konto/betalingskort'] },
          { label: 'Fakturaer', routerLink: ['/konto/fakturarer'] },
        ];
      }

      items = [
        ...items,
        { separator: true } as MenuItem,
        { label: 'Betingelser', routerLink: ['/konto/betingelser'] },
        { separator: true } as MenuItem,
        { label: 'Log ud', command: () => this.logout() },
      ];

      this.accountMenuItems.set(items);
      this.cdr.markForCheck();
    });
  }

  setupBusinessMenuItems(): void {
    const initialItems: ExtendedMenuItem[] = [
      {
        label: 'Alle',
        command: (event) => this.onStoreSelectAction(null),
        active: false,
      },
    ];

    this.businessMenuItems.set(initialItems);

    combineLatest([this.stores$, this.defaultStoreId$])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([stores, defaultStoreId]) => {
        const storeItems: ExtendedMenuItem[] = stores.map((store) => ({
          label: store.attributes.name,
          command: (event) => this.onStoreSelectAction(+store.id),
          active: defaultStoreId === +store.id,
          showWarning: store.attributes.version === 1,
          showSettingsButton: true,
          settingsCommand: (event) => {
            this.onStoreSelectAction(+store.id, '/integrations/' + +store.id);
          },
        }));

        const menuItems: ExtendedMenuItem[] = [{ ...initialItems[0], active: defaultStoreId === null }, ...storeItems];

        if (stores.length > 0) {
          menuItems.push({ separator: true } as ExtendedMenuItem);
        }

        menuItems.push({
          label: 'Tilføj forretning',
          routerLink: ['/stores/sales-platform'],
          showAddIcon: true,
        } as ExtendedMenuItem);

        this.businessMenuItems.set(menuItems);
        this.cdr.markForCheck();
      });
  }

  private updateActiveMenuItems(currentRoute: string): void {
    const routeGroup = this.routeMapping.getRouteGroup(currentRoute);
    const accountingGroup = routeGroup === 'accounting' ? this.routeMapping.getAccountingGroup(currentRoute) : null;
    const toolboxGroup = routeGroup === 'toolbox' ? this.routeMapping.getToolboxGroup(currentRoute) : null;

    const updatedItems = this.panelMenuItems().map((item) => {
      switch (item.id) {
        case 'home':
          return {
            ...item,
            active: routeGroup === 'home',
            expanded: routeGroup === 'home',
          };

        case 'accounting':
          const updatedSubItems =
            item.items?.map((subItem) => ({
              ...subItem,
              active: accountingGroup === subItem.id,
            })) || [];

          return {
            ...item,
            active: routeGroup === 'accounting',
            expanded: routeGroup === 'accounting' || item.expanded,
            items: updatedSubItems,
          };

        case 'toolbox':
          const updatedToolboxItems =
            item.items?.map((subItem) => ({
              ...subItem,
              active: toolboxGroup === subItem.id,
            })) || [];

          return {
            ...item,
            active: routeGroup === 'toolbox',
            expanded: routeGroup === 'toolbox' || item.expanded,
            items: updatedToolboxItems,
          };

        default:
          return item;
      }
    });

    this.panelMenuItems.set(updatedItems);
  }

  navigateToSupportPage(): void {
    const supportLink = this.supportLinkService.getList().mainLayout.helpCenter;
    window.open(supportLink, '_blank', 'noopener,noreferrer');
  }

  navigateToSettingsPage(): void {
    const storeId = this.activeStoreId();
    if (storeId) {
      this.router.navigate(['/integrations/' + storeId]);
    }
  }

  toggleTheme(): void {
    this.themeService.toggleTheme();
  }

  logout() {
    this._authService.logout({
      logoutParams: {
        returnTo: `https://storebuddy.dk`,
      },
    });
  }

  onStoreSelectAction(storeId: number | null, route: string = '/') {
    this.rxStore.dispatch(BootstrapActions.setDefaultStoreById({ storeId: storeId ?? null }));
    this.activeStoreId.set(storeId);

    this.router.navigate([route]);
  }

  isProductionEnvironment(): boolean {
    return environment.environment === 'production';
  }
}
